import React, { useState } from "react";
import { X, Sparkles, Loader2, Languages } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { DynamicListField } from "./DynamicListField";
import { DynamicPairField } from "./DynamicPairField";
import { UploadButton } from "./UploadButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["einfamilienhaus", "reihenhaus", "villa", "stadthaus", "wohnbau", "sanierung"];
const STATUSES = ["Verfügbar", "In Planung", "In Entwicklung"];
const DESC_LANGS = ["de", "en", "ru", "uk"];
const DESC_LANG_LABELS = { de: "DE", en: "EN", ru: "RU", uk: "UK" };

const EMPTY_PROJECT = {
  slug: "",
  title: "",
  location: "",
  status: STATUSES[0],
  year: new Date().getFullYear(),
  category: CATEGORIES[0],
  short_description_de: "",
  short_description_en: "",
  short_description_ru: "",
  short_description_uk: "",
  description_de: "",
  description_en: "",
  description_ru: "",
  description_uk: "",
  cover_image: "",
  images: [],
  specs: [],
  highlights: [],
  video_url: "",
  floorplans: [],
  energy_certificate: "",
};

function specsObjToArray(specs) {
  return Object.entries(specs || {}).map(([key, value]) => ({ key, value }));
}

function specsArrayToObj(arr) {
  const obj = {};
  arr.forEach(({ key, value }) => {
    if (key) obj[key] = value;
  });
  return obj;
}

export default function ProjectFormModal({ project, onClose, onSave, saving }) {
  const { t } = useLang();
  const { authHeaders } = useAuth();
  const [form, setForm] = useState(() =>
    project
      ? { ...EMPTY_PROJECT, ...project, specs: specsObjToArray(project.specs), floorplans: project.floorplans || [] }
      : EMPTY_PROJECT
  );
  const [suggesting, setSuggesting] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [descLang, setDescLang] = useState("de");

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSuggest = async () => {
    if (!form.title || !form.location) {
      toast.error(t("admin.projects.suggest_needs_title_location"));
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/suggest-description`,
        { title: form.title, location: form.location, category: form.category, status: form.status, year: Number(form.year) },
        { headers: authHeaders(), withCredentials: true }
      );
      set("short_description_de", data.short_description);
      toast.success(t("admin.projects.suggest_success"));
    } catch (err) {
      console.warn("Suggestion failed:", err?.response?.status ?? err?.message);
      toast.error(t("admin.projects.suggest_failed"));
    } finally {
      setSuggesting(false);
    }
  };

  const handleTranslate = async () => {
    if (!form.short_description_de || !form.description_de) {
      toast.error(t("admin.projects.translate_needs_de"));
      return;
    }
    setTranslating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/translate-project`,
        { short_description_de: form.short_description_de, description_de: form.description_de },
        { headers: authHeaders(), withCredentials: true }
      );
      setForm((f) => ({ ...f, ...data }));
      toast.success(t("admin.projects.translate_success"));
    } catch (err) {
      console.warn("Translation failed:", err?.response?.status ?? err?.message);
      toast.error(t("admin.projects.translate_failed"));
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      slug: form.slug.trim(),
      year: Number(form.year) || new Date().getFullYear(),
      images: form.images.filter(Boolean),
      highlights: form.highlights.filter(Boolean),
      specs: specsArrayToObj(form.specs),
      floorplans: form.floorplans.filter((fp) => fp.label && fp.url),
      video_url: form.video_url || null,
      energy_certificate: form.energy_certificate || null,
    };
    onSave(payload, project?.id);
  };

  return (
    <div
      data-testid="project-form-modal"
      className="fixed inset-0 z-50 bg-[#020c1f]/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
    >
      <div className="w-full max-w-3xl bg-[#0A2244] border border-white/15 p-6 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <h3 className="font-display text-2xl font-medium tracking-tight">
            {project ? t("admin.projects.form_title_edit") : t("admin.projects.form_title_create")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="project-form-close"
            className="w-9 h-9 border border-white/15 hover:border-white flex items-center justify-center transition-colors"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <TextField testId="project-field-title" label={t("admin.projects.field_title")} value={form.title} onChange={(v) => set("title", v)} required />
            <TextField testId="project-field-slug" label={t("admin.projects.field_slug")} value={form.slug} onChange={(v) => set("slug", v)} required />
            <TextField testId="project-field-location" label={t("admin.projects.field_location")} value={form.location} onChange={(v) => set("location", v)} required />
            <SelectField testId="project-field-status" label={t("admin.projects.field_status")} value={form.status} options={STATUSES} onChange={(v) => set("status", v)} />
            <TextField testId="project-field-year" label={t("admin.projects.field_year")} value={form.year} onChange={(v) => set("year", v)} type="number" required />
            <SelectField testId="project-field-category" label={t("admin.projects.field_category")} value={form.category} options={CATEGORIES} onChange={(v) => set("category", v)} />
            <TextField testId="project-field-cover-image" label={t("admin.projects.field_cover_image")} value={form.cover_image} onChange={(v) => set("cover_image", v)} required extra={
              <UploadButton accept="image/*" onUploaded={(url) => set("cover_image", url)} testId="project-cover-image-upload" />
            } />
            <TextField testId="project-field-video-url" label={t("admin.projects.field_video_url")} value={form.video_url || ""} onChange={(v) => set("video_url", v)} />
            <TextField testId="project-field-energy-certificate" label={t("admin.projects.field_energy_certificate")} value={form.energy_certificate || ""} onChange={(v) => set("energy_certificate", v)} extra={
              <UploadButton accept="application/pdf" onUploaded={(url) => set("energy_certificate", url)} testId="project-energy-certificate-upload" />
            } />
          </div>

          <TextAreaField
            testId="project-field-short-description"
            label={t("admin.projects.field_short_description")}
            value={form.short_description_de}
            onChange={(v) => set("short_description_de", v)}
            required
            rows={2}
            extra={
              <button
                type="button"
                onClick={handleSuggest}
                disabled={suggesting}
                data-testid="project-suggest-description-btn"
                className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-white/60 hover:text-white transition-colors disabled:opacity-50"
              >
                {suggesting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} strokeWidth={1.5} />}
                {t("admin.projects.suggest_button")}
              </button>
            }
          />
          <TextAreaField
            testId="project-field-description"
            label={t("admin.projects.field_description")}
            value={form.description_de}
            onChange={(v) => set("description_de", v)}
            required
            rows={5}
          />

          {/* Multi-language translations of short_description / description */}
          <div className="border border-white/10 p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2" data-testid="project-desc-lang-tabs">
                {DESC_LANGS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setDescLang(l)}
                    data-testid={`project-desc-lang-${l}`}
                    className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase border transition-colors ${
                      descLang === l ? "bg-white text-[#051630] border-white" : "border-white/20 text-white/60 hover:border-white/50"
                    }`}
                  >
                    {DESC_LANG_LABELS[l]}
                    {l !== "de" && form[`short_description_${l}`] ? " ✓" : ""}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleTranslate}
                disabled={translating}
                data-testid="project-translate-btn"
                className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-white/60 hover:text-white transition-colors disabled:opacity-50"
              >
                {translating ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} strokeWidth={1.5} />}
                {t("admin.projects.translate_button")}
              </button>
            </div>

            {descLang === "de" ? (
              <p className="text-xs text-white/40">{t("admin.projects.translate_hint")}</p>
            ) : (
              <div className="space-y-4">
                <TextAreaField
                  testId={`project-field-short-description-${descLang}`}
                  label={`${t("admin.projects.field_short_description")} (${DESC_LANG_LABELS[descLang]})`}
                  value={form[`short_description_${descLang}`]}
                  onChange={(v) => set(`short_description_${descLang}`, v)}
                  rows={2}
                />
                <TextAreaField
                  testId={`project-field-description-${descLang}`}
                  label={`${t("admin.projects.field_description")} (${DESC_LANG_LABELS[descLang]})`}
                  value={form[`description_${descLang}`]}
                  onChange={(v) => set(`description_${descLang}`, v)}
                  rows={5}
                />
              </div>
            )}
          </div>

          <DynamicListField
            testId="project-images"
            label={t("admin.projects.field_images")}
            items={form.images}
            onChange={(v) => set("images", v)}
            placeholder={t("admin.projects.image_url_placeholder")}
            addLabel={t("admin.projects.add_item")}
            uploadAccept="image/*"
          />

          <DynamicListField
            testId="project-highlights"
            label={t("admin.projects.field_highlights")}
            items={form.highlights}
            onChange={(v) => set("highlights", v)}
            placeholder={t("admin.projects.highlight_placeholder")}
            addLabel={t("admin.projects.add_item")}
          />

          <DynamicPairField
            testId="project-specs"
            label={t("admin.projects.field_specs")}
            items={form.specs}
            onChange={(v) => set("specs", v)}
            keyName="key"
            valName="value"
            keyPlaceholder={t("admin.projects.spec_key_placeholder")}
            valPlaceholder={t("admin.projects.spec_value_placeholder")}
            addLabel={t("admin.projects.add_item")}
          />

          <DynamicPairField
            testId="project-floorplans"
            label={t("admin.projects.field_floorplans")}
            items={form.floorplans}
            onChange={(v) => set("floorplans", v)}
            keyName="label"
            valName="url"
            keyPlaceholder={t("admin.projects.floorplan_label_placeholder")}
            valPlaceholder={t("admin.projects.floorplan_url_placeholder")}
            addLabel={t("admin.projects.add_item")}
            valUploadAccept="application/pdf"
          />

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              data-testid="project-form-cancel"
              className="px-6 py-3 border border-white/20 hover:border-white text-xs tracking-[0.18em] uppercase font-medium transition-colors"
            >
              {t("admin.projects.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="project-form-submit"
              className="px-6 py-3 bg-white text-[#051630] text-xs tracking-[0.18em] uppercase font-bold hover:bg-transparent hover:text-white border border-white transition-colors disabled:opacity-50"
            >
              {saving ? t("admin.projects.saving") : t("admin.projects.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, testId, type = "text", required, extra }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          data-testid={testId}
          className="flex-1 bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-white/50 outline-none"
        />
        {extra}
      </div>
    </div>
  );
}

function TextAreaField({ label, value, onChange, testId, rows = 3, required, extra }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-[11px] tracking-[0.2em] uppercase text-white/50">{label}</label>
        {extra}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        rows={rows}
        data-testid={testId}
        className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-white/50 outline-none resize-y"
      />
    </div>
  );
}

function SelectField({ label, value, options, onChange, testId }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-white/50 outline-none"
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#0A2244]">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
